import React, { useEffect, useState } from 'react'
import PillButton from './PillButton'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import LandingHeader from './LandingHeader'
import PublishedStoryCard from './PublishedStoryCard'
import SettingsPanel, { type SettingsSection } from './SettingsPanel'
import { authClient } from '../lib/auth-client'
import type { AppSettings } from '../App'

/**
 * The reader's own saved stories.
 *
 * Distinct from the Library tab on a public author profile: this one is always available to its
 * owner regardless of the library_public setting, and it is the only place a story can be removed
 * from a list rather than from the story page itself.
 */
export default function LibraryView(): React.JSX.Element {
  const { data: session } = authClient.useSession()
  const [stories, setStories] = useState<PublishedStorySummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null)
  const [isProfileLoaded, setIsProfileLoaded] = useState(false)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('account')
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    if (!session?.user) {
      setIsLoading(false)
      return
    }
    let active = true
    window.api.library
      .list()
      .then((items) => {
        if (active) setStories(items)
      })
      .catch((reason) => {
        if (active)
          setError(reason instanceof Error ? reason.message : 'Unable to load your library.')
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [session?.user])

  useEffect(() => {
    if (!session?.user) return
    let active = true
    Promise.all([window.api.profiles.getActive(), window.api.settings.get()])
      .then(([profile, loadedSettings]) => {
        if (!active) return
        setAuthorProfile(profile)
        setSettings(loadedSettings)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsProfileLoaded(true)
      })
    return () => {
      active = false
    }
  }, [session?.user])

  const openSettings = (section: 'account' | 'profile'): void => {
    setSettingsSection(section)
    void window.api.settings
      .get()
      .then((latestSettings) => {
        setSettings(latestSettings)
        setShowSettings(true)
      })
      .catch(() => setShowSettings(true))
  }

  const saveSettings = (updatedSettings: AppSettings): void => {
    setSettings(updatedSettings)
    localStorage.setItem('ninenights-settings', JSON.stringify(updatedSettings))
    void window.api.settings.save(updatedSettings)
  }

  const syncTheme = (theme: AppSettings['theme']): void => {
    setSettings((current) => (current ? { ...current, theme } : current))
  }

  const saveProfile = async (changes: {
    penName: string
    authorBlurb: string
    libraryPublic: boolean
  }): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.update(authorProfile.id, changes)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const uploadAvatar = async (file: File): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.uploadAvatar(authorProfile.id, file)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const removeAvatar = async (): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.removeAvatar(authorProfile.id)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const selectDefaultAvatar = async (defaultAvatarId: string): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.selectDefaultAvatar(
      authorProfile.id,
      defaultAvatarId
    )
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  // Dropped from local state on success only: a failed unsave must leave the row where it is,
  // or the reader is told they removed something they still have.
  const removeStory = async (publicationId: string): Promise<void> => {
    try {
      await window.api.storySocial.unsave(publicationId)
      setStories((current) => current.filter((story) => story.id !== publicationId))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not remove that story.')
    }
  }

  const accountDisplayName =
    authorProfile?.penName.trim() || session?.user?.name?.trim() || undefined
  const isAccountLoading = Boolean(session?.user) && !isProfileLoaded

  return (
    <main className="published-story-shell all-stories-page-shell">
      <LandingHeader
        showNavLinks={false}
        showAppButton={false}
        isAppHeader
        isAccountLoading={isAccountLoading}
        userName={accountDisplayName}
        avatarUrl={authorProfile?.avatarUrl}
        onOpenSettings={openSettings}
        currentTheme={settings?.theme}
        onThemeChange={syncTheme}
      />
      <section className="all-stories-page">
        <VStack gap={6}>
          <VStack gap={1}>
            <h1>My Library</h1>
            <Text type="supporting">Stories you saved, most recent first.</Text>
          </VStack>

          {!session?.user ? (
            <Text type="supporting">Sign in to see the stories you have saved.</Text>
          ) : isLoading ? (
            <Text type="supporting">Loading your library…</Text>
          ) : error ? (
            <Text type="supporting">{error}</Text>
          ) : stories.length === 0 ? (
            <VStack gap={3} align="start">
              <Text type="supporting">
                You have not saved any stories yet. Tap Save on a story to keep it here.
              </Text>
              <PillButton label="Browse stories" href="/home" />
            </VStack>
          ) : (
            <section className="home-feed-list">
              {stories.map((story, index) => (
                <PublishedStoryCard
                  key={story.id}
                  story={story}
                  index={index}
                  actions={
                    <HStack justify="end" className="library-card-actions">
                      <PillButton
                        variant="ghost"
                        size="sm"
                        label="Remove"
                        onClick={() => void removeStory(story.id)}
                      />
                    </HStack>
                  }
                />
              ))}
            </section>
          )}
        </VStack>
      </section>
      {showSettings && settings && (
        <SettingsPanel
          settings={settings}
          profile={authorProfile}
          initialSection={settingsSection}
          onSaveSettings={saveSettings}
          onSaveProfile={saveProfile}
          onUploadAvatar={uploadAvatar}
          onRemoveAvatar={removeAvatar}
          onSelectDefaultAvatar={selectDefaultAvatar}
          onClose={() => setShowSettings(false)}
        />
      )}
    </main>
  )
}
